import { $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import type { LexicalEditor } from 'lexical';

/**
 * Gap 2 — Markdown export for Writer's Lab documents.
 *
 * Spec 11 §1.3 set the v3.0 export ceiling as "export-to-clipboard +
 * export-to-markdown." Two callers:
 *
 *   - Live canvas in WriterLab: reads from the active LexicalEditor so the
 *     user's in-flight edits + structure (headings, lists, etc.) round-trip.
 *   - Persisted document Assets (AssetPreviewModal): TextContent is already
 *     a plain-text mirror, suitable as markdown for the simple paragraph
 *     shape v3.0 produces. Rich-structure round-trip arrives once the BE
 *     persists full EditorState JSON (deferred to v3.0.1).
 */

/** Pulls the markdown rendering of the editor's current state. Must run inside React. */
export function exportEditorToMarkdown(editor: LexicalEditor): string {
  let result = '';
  editor.getEditorState().read(() => {
    result = $convertToMarkdownString(TRANSFORMERS);
  });
  return result;
}

/**
 * Persisted document assets carry both <c>textContent</c> (plain-text mirror)
 * and <c>payloadJson</c> (the operation's block list). v3.0 documents are
 * paragraph-only, so the text mirror == valid markdown. We keep the helper
 * dedicated so callers don't conflate "text content" with "markdown" in their
 * UI copy; later it can switch to a real block-to-markdown converter without
 * touching consumers.
 */
export function assetTextToMarkdown(textContent: string | null | undefined): string {
  if (!textContent) return '';
  return textContent.trim();
}

/** Triggers a browser download of the supplied markdown as a `.md` file. */
export function downloadMarkdown(markdown: string, filenameHint: string): void {
  const safeBase = (filenameHint || 'document')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80) || 'document';
  const filename = safeBase.toLowerCase().endsWith('.md') ? safeBase : `${safeBase}.md`;

  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Copies markdown to the system clipboard. Falls back to a no-op-shaped error
 * when the Clipboard API is unavailable so callers can show a "couldn't copy"
 * toast rather than a thrown promise.
 */
export async function copyMarkdownToClipboard(markdown: string): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    throw new Error('Clipboard API not available in this browser.');
  }
  await navigator.clipboard.writeText(markdown);
}
